import isEmpty from "lodash/isEmpty";
import { Button } from "PFComponents/button";
import { Stack } from "PFComponents/containers";
import { SelectV2 } from "PFComponents/select_v2";
import { fetchCustomValues } from "PFCore/services/custom_values";
import { CustomType, CustomValue } from "PFCore/types";
import { Audience } from "PFTypes";
import { Option } from "PFTypes/options";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

type AudienceSelectorProps = {
  audience: Audience[];
  broadcastTypes: CustomType[];
  onAudienceChange: (audience: Audience) => void;
  disabled?: boolean;
};

type AudienceValue = Pick<CustomValue, "global_id" | "id" | "text" | "value">;

export const AudienceSelector = ({
  audience,
  broadcastTypes,
  onAudienceChange,
  disabled
}: AudienceSelectorProps) => {
  const [selectedFields, setSelectedFields] = useState<Audience>({});
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.privacy" });

  const handleSelection = useCallback(
    (value: AudienceValue[], name: string): void => {
      const updatedSelectedFields = { ...selectedFields };

      if (isEmpty(value) || value === undefined) {
        delete updatedSelectedFields[name];
      } else {
        updatedSelectedFields[name] = value;
      }

      setSelectedFields(updatedSelectedFields);
    },
    [selectedFields]
  );

  const parseOptions = useCallback(
    (options: Option<AudienceValue>[], broadcastType: CustomType): Option<AudienceValue>[] => {
      const audiencesToFilter = audience
        .flatMap((audience) => audience[broadcastType.name])
        .filter((item) => item)
        .map(({ id }) => id);

      return options.filter((option) => !audiencesToFilter.includes(option.id));
    },
    [audience]
  );

  return (
    <Stack alignItems="start">
      {broadcastTypes.map((broadcastType) => (
        <SelectV2<AudienceValue>
          key={broadcastType.id}
          label={broadcastType?.display_as}
          multiple
          disabled={disabled}
          fetchOptions={async ({ searchTerm, ids }) => {
            const result = await fetchCustomValues({
              term: searchTerm,
              customTypeId: broadcastType.id,
              ids: ids
            });
            const entries = result.entries.map((audience) => ({
              id: audience.id,
              displayElement: audience.text,
              value: String(audience.value),
              original: audience as AudienceValue
            }));
            return {
              entries,
              meta: result.meta
            };
          }}
          parseOptions={(options: Option<AudienceValue>[]) => parseOptions(options, broadcastType)}
          onChange={(value: Option<AudienceValue>[]) =>
            handleSelection(
              value.map(({ original }) => original),
              broadcastType.name
            )
          }
          value={(selectedFields[broadcastType.name] || []).map(({ id }) => id)}
        />
      ))}
      <Button
        text={t("addAudience")}
        kind="secondary"
        disabled={disabled || isEmpty(selectedFields)}
        onClick={() => {
          onAudienceChange(selectedFields);
          setSelectedFields({});
        }}
      />
    </Stack>
  );
};
